// The sign-in providers, one strategy each (#5154, founder pick A of the
// "more ways to sign in" mocks, 2026-10-22). The sheet draws whatever this
// registry, the platform's order and the server's answer allow, and never
// branches on a provider's name: a new provider is a new entry here.
//
// Each entry:
//   id          the analytics `method` and the sheet's key
//   firebaseId  Firebase's provider id (a credential's providerId)
//   name        the word in the copy ("Try Apple again")
//   label       the button's title, the brand's own words (Apple allows only
//               Sign in / Sign up / Continue with Apple; Meta prefers
//               Continue with Facebook)
//   inApp       the app's browsers it is offered in (signInTrouble's
//               IN_APP_MARKERS names). Google refuses sign-in in all of them;
//               Apple waits for a real-phone test in each (founder answer 4,
//               2026-10-23); Facebook works inside Facebook's own browser.
//   released    false keeps the button off whatever the server says
//   linkable    an account-exists credential of it may be kept and offered
//               for linking: only a provider that verifies the address
//   make()      a fresh Firebase provider for one sign-in
//   credentialFromError(error)  the pending credential of an
//               account-exists answer, or null
import { FacebookAuthProvider, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { googleProvider } from '../firebase';
import { extraProvidersOn } from './emailLinkSignIn';
import { inAppBrowserName, signInPlatform } from './signInTrouble';

export const PROVIDERS = Object.freeze({
    google: Object.freeze({
        id: 'google', firebaseId: 'google.com', name: 'Google', label: 'Continue with Google',
        inApp: Object.freeze([]), released: true, linkable: true,
        make: () => {
            // Force the account chooser every time (as before #5154).
            googleProvider.setCustomParameters({ prompt: 'select_account' });
            return googleProvider;
        },
        credentialFromError: (e) => GoogleAuthProvider.credentialFromError(e),
    }),
    apple: Object.freeze({
        id: 'apple', firebaseId: 'apple.com', name: 'Apple', label: 'Continue with Apple',
        inApp: Object.freeze([]), released: true, linkable: true,
        make: () => {
            const p = new OAuthProvider('apple.com');
            p.addScope('email');
            p.addScope('name');
            return p;
        },
        credentialFromError: (e) => OAuthProvider.credentialFromError(e),
    }),
    facebook: Object.freeze({
        id: 'facebook', firebaseId: 'facebook.com', name: 'Facebook', label: 'Continue with Facebook',
        inApp: Object.freeze(['Facebook']),
        // Held by the founder, 2026-10-22 (#5154): Firebase marks a Facebook
        // email unverified and every backend refuses an unverified token, so a
        // Facebook student could sign in and then use nothing. Built in full;
        // released once how its email is proven is decided with Meta's setup.
        released: false,
        // Never kept for linking (security review, #5154): Facebook does not
        // verify the address, so on a shared computer a credential left by
        // someone else's Facebook account with the student's address could
        // be linked by the student's own yes. Facebook joins an account only
        // from a fresh sign-in, later.
        linkable: false,
        make: () => {
            const p = new FacebookAuthProvider();
            p.addScope('email');
            return p;
        },
        credentialFromError: (e) => FacebookAuthProvider.credentialFromError(e),
    }),
});

// The order per platform (founder pick A; answer 2, 2026-10-23: Google first
// on desktop and anything we cannot tell). The first slot is the account
// that device is already signed in to. A new platform is a new row.
export const PROVIDER_ORDER = Object.freeze({
    ios: Object.freeze(['apple', 'google', 'facebook']),
    android: Object.freeze(['google', 'apple', 'facebook']),
    desktop: Object.freeze(['google', 'apple', 'facebook']),
    unknown: Object.freeze(['google', 'apple', 'facebook']),
});

/** The provider for a Firebase provider id ('apple.com'), or null. */
export function providerForFirebaseId(firebaseId) {
    return Object.values(PROVIDERS).find((p) => p.firebaseId === firebaseId) || null;
}

/** The analytics method for a Firebase provider id; 'google' when unknown,
 *  which is what every sign-in was before #5154. */
export function methodForFirebaseId(firebaseId) {
    return providerForFirebaseId(firebaseId)?.id || 'google';
}

export const providerName = (id) => PROVIDERS[id]?.name || 'Google';

/**
 * The provider ids to draw, in order. Pure over its inputs:
 *   platform  signInPlatform()
 *   app       inAppBrowserName() (null outside an app's browser)
 *   on        which providers the server and flag allow ({apple, facebook});
 *             Google is always on outside an app's browser
 *   exclude   ids to leave out (the one an account-exists answer refused)
 *   registry  PROVIDERS (a test passes a released Facebook)
 */
export function providerList({ platform = 'unknown', app = null, on = {}, exclude = [], registry = PROVIDERS } = {}) {
    const order = PROVIDER_ORDER[platform] || PROVIDER_ORDER.unknown;
    return order.filter((id) => {
        const p = registry[id];
        if (!p || !p.released || exclude.includes(id)) return false;
        if (app && !p.inApp.includes(app)) return false;
        return id === 'google' || on[id] === true;
    });
}

/** The list for this browser, now. */
export function providersHere({ exclude = [] } = {}) {
    return providerList({ platform: signInPlatform(), app: inAppBrowserName(), on: extraProvidersOn(), exclude });
}

/** True when the sheet draws anything beyond Google: the #5154 layout. */
export const moreThanGoogle = (list) => (list || []).some((id) => id !== 'google');
